import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import type { RestAPIEndpoints } from '@lifeomic/react-client/rest-api-types';

export type ZoriPulseAutoScanEligibility =
  RestAPIEndpoints['GET /v1/agents-v2/zori-pulse/subjects/:subject_id/auto-scan-eligibility']['Response'];

export type ZoriPulseAutoScanSkipReason =
  ZoriPulseAutoScanEligibility['skip_reasons'][number];

export type UsePulseAutoScanEligibilityArgs = {
  subjectId: string;
  /** Gate: only fetch when the operator can already see Pulse controls. */
  enabled: boolean;
};

export type UsePulseAutoScanEligibility = {
  eligibility: ZoriPulseAutoScanEligibility | null;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Fetches automatic Pulse enrollment eligibility for a member chart.
 *
 * Uses the same Zori-audit-gated agents endpoint as ops debugging. Failures
 * (including 403 when the caller lacks audit permission) surface as
 * ``isError`` so the UI can hide the caption rather than block the page.
 */
export const usePulseAutoScanEligibility = ({
  subjectId,
  enabled,
}: UsePulseAutoScanEligibilityArgs): UsePulseAutoScanEligibility => {
  const query = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/zori-pulse/subjects/:subject_id/auto-scan-eligibility',
    { subject_id: subjectId },
    {
      enabled: enabled && Boolean(subjectId),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  return {
    eligibility: query.data ?? null,
    isLoading: Boolean(enabled) && query.isLoading,
    isError: Boolean(query.isError),
  };
};

/** Human-readable label for a single enrollment skip reason. */
export const formatAutoScanSkipReason = (
  reason: ZoriPulseAutoScanSkipReason,
): string => {
  switch (reason) {
    case 'auto_scan_disabled':
      return 'auto scan disabled';
    case 'no_membership_tier':
      return 'no membership tier';
    case 'tier_not_in_config':
      return 'tier not configured for auto scan';
    case 'no_maintained_cohort':
      return 'no maintained cohort for tier';
    case 'not_in_maintained_cohort':
      return 'not in maintained cohort';
    case 'in_flight':
      return 'scan already in flight';
    case 'not_due':
      return 'not due yet';
    case 'would_skip_inactive':
      return 'inactive member';
    case 'journey_unavailable':
      return 'member journey unavailable';
  }
};

/** One-line summary for the Pulse review header caption. */
export const formatAutoScanEligibilitySummary = (
  eligibility: ZoriPulseAutoScanEligibility,
): string => {
  if (eligibility.would_enqueue) {
    return 'Automatic Pulse: would enqueue';
  }
  const reasons = eligibility.skip_reasons
    .map(formatAutoScanSkipReason)
    .filter(Boolean);
  if (reasons.length === 0) {
    return 'Automatic Pulse: would not enqueue';
  }
  return `Automatic Pulse: skipped — ${reasons.join('; ')}`;
};
